import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { decryptText, encryptText } from '../../utils/utils';

export type ITRFileType = {
    case_id: string;
    status: string;
    steps: number;
    basic_details: any;
    documents: any;
    portal_credential: any;
    payments: any;

};

// Helper function to load state from localStorage
const loadState = (): ITRFileType => {
    try {
        const serializedState = localStorage.getItem('itrFile');
        if (serializedState === null) {
            return {
                basic_details: {},
                portal_credential: {},
                documents: null,
                payments: [],
                steps: 0,
                case_id: "",
                status: "",
            };
        }
        const state = JSON.parse(decryptText(serializedState));
        return {
            ...state,
            steps: state.steps ?? 0,
            documents: state.documents || null,
        };
    } catch (err) {
        return {
            basic_details: {},
            portal_credential: {},
            documents: null,
            payments: [],
            steps: 0,
            case_id: "",
            status: "",
        };
    }
};

// Helper function to save state to localStorage
const saveState = (state: ITRFileType) => {
    try {
        const serializedState = encryptText(JSON.stringify(state));
        localStorage.setItem('itrFile', serializedState);
    } catch (err) {
        // Ignore write errors
    }
};

const initialState: ITRFileType = loadState();

const itrFileSlice = createSlice({
    name: 'itrFile',
    initialState,
    reducers: {
        setItrDetails: (state, action: PayloadAction<any>) => {
            state.case_id = action.payload.case_id;
            state.status = action.payload.status;
            state.steps = action.payload.steps;
            state.basic_details = action.payload.basic_details;
            state.documents = action.payload.documents;
            state.portal_credential = action.payload.portal_credential;
            state.payments = action.payload.payments;
            saveState(state);
        },
        setBasicDetails: (state, action: PayloadAction<any>) => {
            state.basic_details = action.payload;
            state.status = action.payload.status;
            saveState(state);
        },
        setPortalCredentials: (state, action: PayloadAction<any>) => {
            state.portal_credential = action.payload;
            state.status = action.payload.status;
            saveState(state);
        },
        setDocuments: (state, action: PayloadAction<any>) => {
            // This handles the full response object { body, section_requirements, status, case_id }
            state.documents = {
                body: action.payload.body || {},
                requirements: action.payload.section_requirements || [],
                status: action.payload.status,
                case_id: action.payload.case_id
            };
            state.status = action.payload.status;
            saveState(state);
        },
        setPaymentProof: (state, action: PayloadAction<any>) => {
            state.payments = action.payload;
            state.status = action.payload.status;
            saveState(state);
        },
        setFilingStep: (state, action: PayloadAction<number>) => {
            state.steps = action.payload;
            saveState(state);
        },
        setCompleted: (state, action: PayloadAction<{ [k: number]: boolean }>) => {
            saveState(state);
        },
        resetITRFile: (state) => {
            state.basic_details = {};
            state.portal_credential = {};
            state.documents = null;
            state.payments = [];
            state.steps = 0;
            state.case_id = "";
            state.status = "";
            localStorage.removeItem('itrFile');
        }
    },
});

export const {
    setBasicDetails,
    setPortalCredentials,
    setDocuments,
    setPaymentProof,
    setFilingStep,
    setCompleted,
    resetITRFile
} = itrFileSlice.actions;

export default itrFileSlice.reducer;

